import {
    action,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { Moment } from "moment";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import {
    AgentCoachingDetails,
    AgentCoachingService,
} from "../../../../services/AgentCoachingService";
import { BaseStore } from "../../../../stores/BaseStore";
import {
    DatePickerComponentStore,
    DateReferenceOption,
} from "../../../../stores/ComponentStores/DatePickerComponentStore";
import { OrgSelectorComponentStore } from "../../../../stores/ComponentStores/OrgSelectorComponentStore";
import { toIsoByDateReference } from "../../../../utils/DateTimeUtils";
import AcxDataGridStore from "../../../UI/AcxDataGrid/AcxDataGridStore";
import { dateColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import { GridValidRowModel } from "@mui/x-data-grid-pro";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import moment from "moment";

interface DgData {
    rows: GridValidRowModel[];
    cols: IColDef[];
}

export enum AgentCoachingTabs {
    Agents,
    AgentManagers,
}

@AcxStore
export class AgentCoachingStore extends BaseStore {
    private readonly agentCoachingService: AgentCoachingService =
        new AgentCoachingService();
    readonly dgStore = new AcxDataGridStore(
        "agentCoachingStore",
        "AgentCoaching",
    );

    readonly managerDgStore = new AcxDataGridStore(
        "agentManagerCoachingStore",
        "AgentCoaching",
    );

    @observable orgSelectorStore: OrgSelectorComponentStore =
        new OrgSelectorComponentStore();
    readonly datePickerStore = new DatePickerComponentStore(
        moment().startOf("M"),
        moment().endOf("d"),
        DateReferenceOption.InteractionDate,
    );

    @observable coachingDetails: AgentCoachingDetails[] = [];
    @observable scoredModuleNames?: string[];
    @observable showMachineEvals?: boolean = false;

    @observable
    currentTab: number = AgentCoachingTabs.Agents;

    constructor(private rootStore: IRootStore) {
        super("Agent Coaching Store");
        makeObservable(this);

        this.dgStore.checkboxSelection = false;
        this.dgStore.heightOverride = "100%";

        this.managerDgStore.checkboxSelection = false;
        this.managerDgStore.heightOverride = "100%";

        this.datePickerStore.setDateRangePreset("This Month");

        reaction(
            (r) => this.anyTaskLoading,
            (arg) => {
                this.dgStore.setLoading(arg);
                this.managerDgStore.setLoading(arg);
            },
        );

        reaction(
            (r) => ({
                orgId: this.orgSelectorStore.orgId,
                startDate: this.datePickerStore.beginDate,
                endDate: this.datePickerStore.endDate,
                activeLocation: this.rootStore.activeLocation,
                dateReference: this.datePickerStore.referenceOption,
                showMachineEvals: this.showMachineEvals,
            }),
            (arg) => {
                if (
                    arg.activeLocation &&
                    arg.activeLocation.location !== "/app/agentcoaching"
                ) {
                    return;
                }

                if (!arg.orgId) {
                    return;
                }
                const orgId = arg.orgId;
                this.setupAsyncTask("Load Agent Coaching Metrics", () =>
                    this.loadAgents(
                        orgId,
                        arg.startDate,
                        arg.endDate,
                        arg.dateReference,
                        arg.showMachineEvals,
                    ),
                );
            },
            { fireImmediately: true },
        );
    }

    getManagerDgData(inputValue: DgData): DgData {
        const newValue = { ...inputValue };

        const { rows, cols } = newValue;

        const managerGroups = rows.reduce((acc, row) => {
            const managerId = row.managerId || "Unassigned";
            acc[managerId] = acc[managerId] || [];
            acc[managerId].push(row);
            return acc;
        }, {});

        const sumCols = cols.filter((col) => {
            return (
                col.field === "acknowledgedEvals" ||
                col.field === "coachedEvals" ||
                col.field === "completedEvals" ||
                col.field === "acknowledgedCoaching"
            );
        });

        const scoreCols = cols.filter((col) =>
            col.headerName?.includes(" Score"),
        );

        const managerRows = Object.entries(managerGroups).map(
            ([managerId, groupRows]) => {
                const managerFullName = groupRows[0].managerFullName;
                const managerRow = {
                    managerFullName:
                        managerId === "Unassigned"
                            ? "Unassigned"
                            : managerFullName,
                    id: managerId,
                };

                // Sum columns
                for (const { field } of sumCols) {
                    managerRow[field] = groupRows.reduce(
                        (sum, row) => sum + (Number(row[field]) || 0),
                        0,
                    );
                }

                // Average score columns
                for (const { field } of scoreCols) {
                    const validValues = groupRows
                        .map((r) => Number(r[field]))
                        .filter((v) => !isNaN(v));
                    managerRow[field] =
                        validValues.length > 0
                            ? validValues.reduce((a, b) => a + b, 0) /
                              validValues.length
                            : null;
                }

                return managerRow;
            },
        );

        newValue.rows = managerRows;

        const removedHeaderNames = [
            "Coaching Progress",
            "Hierarchies",
            "Full Name",
            "Id",
            "Start Date",
        ];

        newValue.cols = cols.filter((col) => {
            if (!col.headerName) return false;
            return !removedHeaderNames.includes(col.headerName);
        });

        return newValue;
    }

    setCurrentTab(value: AgentCoachingTabs) {
        this.currentTab = value;
    }

    private async loadAgents(
        orgId: string,
        startDate: Moment,
        endDate: Moment,
        dateReference: DateReferenceOption,
        showMachineEvals?: boolean,
    ) {
        const { beginDate: start, endDate: end } = toIsoByDateReference(
            startDate,
            endDate,
            dateReference,
        );

        const res = await this.agentCoachingService.getAgents(
            start!,
            end!,
            dateReference,
            showMachineEvals,
        );

        //Need this here to get the dates formatted and filter
        res.cols = res.cols.map((item) => {
            if (item.field === "startDate") {
                return {
                    ...item,
                    ...dateColumnType,
                };
            }

            return item;
        });

        runInAction(() => {
            const newAgentData: DgData = { rows: res.rows, cols: res.cols };

            const managerDgData = this.getManagerDgData(newAgentData);

            this.dgStore.rows = newAgentData.rows;
            this.dgStore.setColumns(newAgentData.cols, true);

            this.managerDgStore.rows = managerDgData.rows;
            this.managerDgStore.setColumns(managerDgData.cols, true);

            this.coachingDetails = res.details;
        });
    }

    @action
    toggleShowMachineEvals = () => {
        this.showMachineEvals = !this.showMachineEvals;
    };
}
